import React from 'react';
import { AnimatedCounter } from './AnimatedCounter';

export const EventCard = ({ event, onEventClick, formatCurrency, formatDate }) => {
  return (
    <div
      className="group relative bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2 border border-gray-200 overflow-hidden animate-fadeIn"
      onClick={() => onEventClick(event)}
    >
      {/* Gradient Top Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      <div className="relative p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-5">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold rounded-full bg-gradient-to-r from-blue-100 to-blue-50 px-3 py-1.5 text-blue-600 border border-blue-200 shadow-sm">
                #{event.id}
              </span>
              <span className="text-xs text-gray-500 flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDate(event.date)}
              </span>
            </div>
            <h3 className="text-base font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 mb-2 line-clamp-1">
              {event.name}
            </h3>
            <p className="text-sm text-gray-600 flex items-center gap-1.5">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {event.venue}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 group/stat hover:shadow-md transition-all">
            <div className="text-sm font-bold text-blue-600 mb-1">
              {event.totalParticipants === 0 ? "∞" : <AnimatedCounter value={event.totalParticipants} />}
            </div>
            <div className="text-xs font-medium text-blue-700">Capacity</div>
          </div>
          <div className="text-center p-3 bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-100 group/stat hover:shadow-md transition-all">
            <div className="text-xl font-bold text-green-600 mb-1">
              <AnimatedCounter value={event.successCount} />
            </div>
            <div className="text-xs font-medium text-green-700">Paid</div>
          </div>
          <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-100 group/stat hover:shadow-md transition-all">
            <div className="text-xl font-bold text-purple-600 mb-1">
              <AnimatedCounter value={event.successCount + event.cancelledCount} />
            </div>
            <div className="text-xs font-medium text-purple-700">Total</div>
          </div>
        </div>

        {/* Amount Summary */}
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-200 mb-5">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">Paid:</span>
            </div>
            <span className="text-lg font-bold text-green-600">
              {formatCurrency(event.successAmount)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">Not Paid:</span>
            </div>
            <span className="text-lg font-bold text-orange-600">
              {formatCurrency(event.cancelledAmount)}
            </span>
          </div>
        </div>

        {/* CTA Button */}
        <button className="w-full group/btn bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-white opacity-0 group-hover/btn:opacity-10 transition-opacity duration-300"></div>
          <span className="relative flex items-center justify-center gap-2">
            View Details
            <svg className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};